// Web Push im Browser: Unterstützung prüfen, abonnieren (nur aus einer Nutzer-Geste!), abmelden, App-Badge setzen.
const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Warum Push hier (noch) nicht geht – für eine verständliche Anleitung
export const pushAvailability = () => {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return { ok: false, reason: 'unsupported' };
  if (isIOS() && !isStandalone()) return { ok: false, reason: 'ios_not_installed' };
  if (!('PushManager' in window)) return { ok: false, reason: 'unsupported' };
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  return { ok: true };
};

const b64ToBytes = (value) => {
  const pad = '='.repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export const currentSubscription = async () => {
  if (!pushSupported()) return null;
  // `ready` wartet ewig, wenn kein Service Worker registriert werden konnte → nach 3 s aufgeben
  const reg = await Promise.race([navigator.serviceWorker.ready, new Promise(r => setTimeout(() => r(null), 3000))]);
  return reg ? reg.pushManager.getSubscription() : null;
};

// Muss direkt aus einem Klick/Tipp aufgerufen werden (iOS-Pflicht)
export const enablePush = async () => {
  const avail = pushAvailability();
  if (!avail.ok) throw new Error(avail.reason);
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await fetch(`${API}/api/push/public-key`, { headers: authHeaders() }).then(r => r.json());
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(publicKey) });
  const json = sub.toJSON();
  const res = await fetch(`${API}/api/push/subscribe`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, standalone: isStandalone() }) });
  if (!res.ok) throw new Error('server');
  return res.json();
};

export const disablePush = async () => {
  const sub = await currentSubscription();
  if (!sub) return { ok: true };
  const json = sub.toJSON();
  await fetch(`${API}/api/push/subscribe`, { method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }) }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
  return { ok: true };
};

export const fetchPushStatus = async () => {
  const sub = await currentSubscription().catch(() => null);
  const ep = sub ? encodeURIComponent(sub.endpoint) : '';
  const res = await fetch(`${API}/api/push/status?endpoint=${ep}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('status');
  return res.json();
};

// Zähler auf dem App-Symbol (iOS 16.4+ Homescreen-App, Android, Desktop)
export const syncAppBadge = (count) => {
  try {
    if (!('setAppBadge' in navigator)) return;
    if (count > 0) navigator.setAppBadge(count).catch(() => {});
    else navigator.clearAppBadge?.().catch(() => {});
  } catch { /* nicht verfügbar */ }
};
